import React, { useState, useEffect } from 'react';
import {
  Monitor, AlertTriangle, Package, ShieldCheck,
  FileSpreadsheet, FileText, Download, RefreshCw,
  Calendar, Filter, ChevronDown, ChevronUp, BarChart2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

// ── Helpers ───────────────────────────────────────────────────────────────────
function buildQS(params) {
  const qs = Object.entries(params).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
  return qs ? '?' + qs : '';
}

async function downloadFile(url, filename) {
  const token = localStorage.getItem('token');
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Error ${res.status}`);
  const rowCount = res.headers.get('X-Row-Count');
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revocar en el acto puede cancelar la descarga en Chrome: se libera después.
  setTimeout(() => URL.revokeObjectURL(href), 10000);
  return rowCount == null ? null : parseInt(rowCount, 10);
}

function today() { return new Date().toISOString().slice(0, 10); }

// Nombre único por descarga (filtros + hora) para no confundir con archivos viejos.
function exportName(id, params, format) {
  const parts = [id, params.status, params.priority, params.from, params.to]
    .filter(Boolean).map(v => String(v).replace(/[^A-Za-z0-9_-]/g, ''));
  const d = new Date();
  const hhmm = String(d.getHours()).padStart(2, '0') + String(d.getMinutes()).padStart(2, '0');
  return `${parts.join('_')}_${today()}_${hhmm}.${format}`;
}
function isoDate(d) { return d ? new Date(d).toISOString().slice(0, 10) : ''; }

// ── Sub-componentes ───────────────────────────────────────────────────────────
function DateRange({ from, to, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <Calendar size={13} style={{ color: 'var(--ink-3)', flexShrink: 0 }} />
      <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>Período:</span>
      <input type="date" value={from} onChange={e => onChange('from', e.target.value)}
        max={to || today()}
        style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--ink-2)' }} />
      <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>→</span>
      <input type="date" value={to} onChange={e => onChange('to', e.target.value)}
        min={from} max={today()}
        style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--ink-2)' }} />
      {(from || to) && (
        <button onClick={() => { onChange('from', ''); onChange('to', ''); }}
          style={{ fontSize: 11, color: 'var(--ink-3)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
          limpiar
        </button>
      )}
    </div>
  );
}

function DownloadBtn({ format, loading, onClick }) {
  const cfg = {
    xlsx: { label: 'Excel', icon: FileSpreadsheet, color: 'var(--green)', bg: 'var(--green-dim)', border: 'var(--green-dim)' },
    csv:  { label: 'CSV',   icon: FileText,        color: 'var(--ink-2)', bg: 'var(--bg-subtle)', border: 'var(--border)' },
  };
  const c = cfg[format];
  const Icon = c.icon;
  return (
    <button onClick={onClick} disabled={loading}
      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: loading ? 'var(--bg)' : c.bg, color: loading ? 'var(--ink-3)' : c.color, border: `1px solid ${loading ? 'var(--border)' : c.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', transition: 'all .15s' }}>
      {loading
        ? <><RefreshCw size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> Generando...</>
        : <><Icon size={14} /> {c.label}</>}
    </button>
  );
}

function ReportCard({ icon: Icon, iconColor, iconBg, title, description, count, countLabel, filters, onDownload, loading }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '20px 24px' }}>
        <div style={{ width: 46, height: 46, borderRadius: 12, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={22} style={{ color: iconColor }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>{title}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>{description}</div>
        </div>
        {count != null && (
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ink)', lineHeight: 1 }}>{count.toLocaleString()}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>{countLabel}</div>
          </div>
        )}
      </div>

      {/* Filtros */}
      <div style={{ borderTop: '1px solid var(--bg)', padding: '12px 24px', background: 'var(--bg-subtle)' }}>
        <button onClick={() => setOpen(o => !o)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--ink-2)', fontWeight: 500, padding: 0 }}>
          <Filter size={12} />
          Filtros
          {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>

        {open && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filters}
          </div>
        )}
      </div>

      {/* Botones descarga */}
      <div style={{ padding: '16px 24px', display: 'flex', gap: 10, flexWrap: 'wrap', borderTop: '1px solid var(--bg)' }}>
        <DownloadBtn format="xlsx" loading={loading === 'xlsx'} onClick={() => onDownload('xlsx')} />
        <DownloadBtn format="csv"  loading={loading === 'csv'}  onClick={() => onDownload('csv')}  />
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function Reports() {
  const [preview, setPreview]     = useState({});
  const [loading, setLoading]     = useState({});
  const [error, setError]         = useState('');
  const [notice, setNotice]       = useState(null);   // { kind: 'ok'|'empty', text }

  // Filtros por reporte
  const [assetF,   setAssetF]   = useState({ from: '', to: '', type: '', location: '' });
  const [incF,     setIncF]     = useState({ from: '', to: '', status: '', priority: '', date_field: '' });
  const [swF,      setSwF]      = useState({ from: '', to: '' });
  const [hwF,      setHwF]      = useState({ from: '', to: '' });
  const [mxF,      setMxF]      = useState({ location: '' });
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/reports/preview`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setPreview).catch(() => {});
    fetch(`${API_URL}/locations`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setLocations(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const dl = async (id, format, params) => {
    const key = `${id}-${format}`;
    setLoading(l => ({ ...l, [key]: format }));
    setError('');
    setNotice(null);
    try {
      const url = `${API_URL}/reports/${id}/${format}${buildQS(params)}`;
      const n = await downloadFile(url, exportName(id, params, format));
      if (n === 0) setNotice({ kind: 'empty', text: '0 resultados con estos filtros: el archivo descargado solo tiene encabezados.' });
      else if (n != null) setNotice({ kind: 'ok', text: `${n.toLocaleString()} registro(s) exportados.` });
    } catch (e) {
      setError(`Error generando reporte de ${id}: ${e.message}`);
    } finally {
      setLoading(l => ({ ...l, [key]: null }));
    }
  };

  const upd = (setter) => (key, val) => setter(f => ({ ...f, [key]: val }));

  return (
    <div style={{ maxWidth: 860 }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <Download size={22} style={{ opacity: 0.7 }} />
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Reportes</h1>
        </div>
        <p style={{ margin: 0, color: 'var(--ink-2)', fontSize: 13 }}>
          Exportá información del sistema en Excel o CSV con filtros por fecha y estado.
        </p>
      </div>

      {error && (
        <div style={{ background: 'var(--red-dim)', border: '1px solid var(--red-dim)', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: 'var(--red)', marginBottom: 20 }}>
          {error}
        </div>
      )}
      {notice && (
        <div style={{ background: notice.kind === 'empty' ? 'var(--amber-dim)' : 'var(--green-dim)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: notice.kind === 'empty' ? 'var(--amber)' : 'var(--green)', marginBottom: 20 }}>
          {notice.text}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Activos */}
        <ReportCard
          icon={Monitor} iconColor="var(--accent)" iconBg="#E6F1FB"
          title="Inventario de Activos"
          description="Equipos con hardware, SO, usuario, ubicación, estado de parches y salud HW."
          count={preview.assets} countLabel="activos totales"
          loading={loading['assets-xlsx'] || loading['assets-csv']}
          onDownload={fmt => dl('assets', fmt, assetF)}
          filters={
            <>
              <DateRange from={assetF.from} to={assetF.to} onChange={upd(setAssetF)} />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <select value={assetF.type} onChange={e => setAssetF(f => ({ ...f, type: e.target.value }))}
                  style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--ink-2)' }}>
                  <option value="">Todos los tipos</option>
                  <option value="physical">Físico</option>
                  <option value="virtual">Virtual</option>
                </select>
              </div>
            </>
          }
        />

        {/* Incidentes */}
        <ReportCard
          icon={AlertTriangle} iconColor="#D45D00" iconBg="var(--amber-dim)"
          title="Reporte de Incidentes"
          description="Tickets con prioridad, estado, técnico asignado, equipo afectado y fechas."
          count={preview.incidents} countLabel="incidentes totales"
          loading={loading['incidents-xlsx'] || loading['incidents-csv']}
          onDownload={fmt => dl('incidents', fmt, incF)}
          filters={
            <>
              <DateRange from={incF.from} to={incF.to} onChange={upd(setIncF)} />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <select value={incF.date_field} onChange={e => setIncF(f => ({ ...f, date_field: e.target.value }))}
                  style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--ink-2)' }}>
                  <option value="">Fecha por: creación</option>
                  <option value="resolved">Fecha por: resolución</option>
                </select>
                <select value={incF.status} onChange={e => setIncF(f => ({ ...f, status: e.target.value }))}
                  style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--ink-2)' }}>
                  <option value="">Todos los estados</option>
                  <option value="finalizados">Finalizados (resuelto + cerrado)</option>
                  <option value="nuevo">Nuevo</option>
                  <option value="en_progreso">En progreso</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="resuelto">Resuelto</option>
                  <option value="cerrado">Cerrado</option>
                </select>
                <select value={incF.priority} onChange={e => setIncF(f => ({ ...f, priority: e.target.value }))}
                  style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--ink-2)' }}>
                  <option value="">Todas las prioridades</option>
                  <option value="critica">Crítica</option>
                  <option value="alta">Alta</option>
                  <option value="media">Media</option>
                  <option value="baja">Baja</option>
                </select>
              </div>
            </>
          }
        />

        {/* Software */}
        <ReportCard
          icon={Package} iconColor="var(--ink-2)" iconBg="var(--bg-subtle)"
          title="Software instalado"
          description="Software detectado por el agente en cada equipo, con versión y fecha de detección."
          count={preview.software} countLabel="títulos únicos"
          loading={loading['software-xlsx'] || loading['software-csv']}
          onDownload={fmt => dl('software', fmt, swF)}
          filters={<DateRange from={swF.from} to={swF.to} onChange={upd(setSwF)} />}
        />

        {/* Licencias */}
        <ReportCard
          icon={FileSpreadsheet} iconColor="#0369a1" iconBg="#e0f2fe"
          title="Licencias de software"
          description="Estado de licencias: total de asientos, en uso, disponibles, vencimiento y costo."
          count={preview.licenses} countLabel="licencias registradas"
          loading={loading['licenses-xlsx'] || loading['licenses-csv']}
          onDownload={fmt => dl('licenses', fmt, {})}
          filters={<span style={{ fontSize: 12, color: 'var(--ink-3)' }}>Sin filtros adicionales para este reporte.</span>}
        />

        {/* Salud HW */}
        <ReportCard
          icon={ShieldCheck} iconColor="var(--green)" iconBg="var(--green-dim)"
          title="Salud del Hardware"
          description="Score de salud, temperatura CPU, uso de RAM, throttling y desgaste de batería por equipo físico."
          count={null}
          loading={loading['hw-health-xlsx'] || loading['hw-health-csv']}
          onDownload={fmt => dl('hw-health', fmt, hwF)}
          filters={<DateRange from={hwF.from} to={hwF.to} onChange={upd(setHwF)} />}
        />

        {/* Matriz de Criticidad */}
        <ReportCard
          icon={BarChart2} iconColor="var(--red)" iconBg="var(--red-dim)"
          title="Matriz de Criticidad"
          description="Activos con nivel de criticidad, ambiente, DRP, disponibilidad, RTO e impacto en el negocio. Ordenados por nivel: Crítica → Alta → Media → Baja."
          count={preview.assets} countLabel="activos totales"
          loading={loading['criticality-xlsx'] || loading['criticality-csv']}
          onDownload={fmt => dl('criticality', fmt, mxF)}
          filters={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <select value={mxF.location} onChange={e => setMxF(f => ({ ...f, location: e.target.value }))}
                style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--ink-2)', maxWidth: 260 }}>
                <option value="">Todas las localidades</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>El Excel incluye colores por nivel de criticidad y resumen al pie.</span>
            </div>
          }
        />

      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
